import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Popconfirm, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, Link } from 'react-router-dom';
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
  SwapOutlined,
} from '@ant-design/icons';

import useCan from '@shared/permissions/useCan';
import ListPageHeader from '@shared/ListPageHeader';
import { DiffCell, CustomAttributesDiffCell } from '@components/shared/inventory/InventoryDiffCell';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import productService from '@services/operator/productService';
import {
  inventoryTransformationService,
  type InventoryTransformationDetail,
  type InventoryTransformationItem,
} from '@services/operator/inventoryTransformationService';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'applied':           return 'success';
    case 'partially_applied': return 'warning';
    case 'failed':            return 'error';
    case 'rejected':          return 'error';
    case 'pending':           return 'default';
    case 'processing':        return 'processing';
    default:                  return 'default';
  }
};

export default function InventoryTransformationShow() {
  const transformation = useLoaderData() as InventoryTransformationDetail;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();
  const [loading, setLoading] = useState<boolean>(false);

  // Detail payload 沒帶 merchant_id；走純前端解：用第一筆 item 的 product_id
  // 反查 product 拿 merchant_id，再餵給 useInventoryAttributeDefinitions 把
  // 自訂屬性的 raw key 翻成 label。失敗就回落到 raw key（hook 內已處理）。
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const firstProductId = transformation.items[0]?.product_id;
  useEffect(() => {
    if (!firstProductId) return;
    let cancelled = false;
    productService.get(firstProductId)
      .then(res => {
        if (cancelled) return;
        if (res.success && res.data?.merchant_id) setMerchantId(res.data.merchant_id);
      })
      .catch(err => console.error('Failed to resolve merchant for attribute labels', err));
    return () => { cancelled = true; };
  }, [firstProductId]);
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', merchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  );

  const canWrite = can('inventories:write');
  const showPartialBanner =
    transformation.status === 'partially_applied' || transformation.status === 'failed';

  // 把後端 error code + interpolations 翻成 i18n 訊息。涵蓋兩個路徑：
  //   1. 422 envelope (response.success === false)：傳入 { message, interpolations }
  //   2. operatorApiFetch 對 4xx/5xx throw 出的 Error：errorCode / interpolations 被攤在
  //      Error 物件頂層（見 services/operator/apiService.ts:113-164）；**不是** axios-style
  //      `err.response.data`。
  // 找不到 code 對應 i18n key 時 fallback 到通用訊息，避免直接顯示 raw error code。
  const translateApiError = (
    source: unknown,
  ): string => {
    type FetchError = Error & { errorCode?: string; interpolations?: Record<string, unknown> };
    type Envelope = { message?: string; interpolations?: Record<string, unknown> };
    const fallback = t('common.message.operation_failed');

    let code: string | undefined;
    let interpolations: Record<string, unknown> | undefined;

    if (source instanceof Error) {
      const e = source as FetchError;
      code = e.errorCode;
      interpolations = e.interpolations;
    } else if (source && typeof source === 'object') {
      const e = source as Envelope;
      code = e.message;
      interpolations = e.interpolations;
    }

    if (!code) return fallback;
    return t(`errors.${code}`, { ...(interpolations ?? {}), defaultValue: fallback });
  };

  const handleApprove = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await inventoryTransformationService.approve(transformation.id);
      if (response.success) {
        message.success(t('common.message.operation_success'));
        navigate(0);
      } else {
        message.error(translateApiError(response));
      }
    } catch (error) {
      console.error('[InventoryTransformationShow] approve failed:', error);
      message.error(translateApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await inventoryTransformationService.reject(transformation.id);
      if (response.success) {
        message.success(t('common.message.operation_success'));
        navigate(0);
      } else {
        message.error(translateApiError(response));
      }
    } catch (error) {
      console.error('[InventoryTransformationShow] reject failed:', error);
      message.error(translateApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('models.product.name'),
      key: 'product',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <Link to={`/products/${r.product_id}`}>
          <div>{r.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.product_name}</div>
        </Link>
      ),
    },
    {
      title: t('models.inventory.custom_attributes'),
      key: 'custom_attributes',
      width: 200,
      render: (_: unknown, r: InventoryTransformationItem) => (
        <CustomAttributesDiffCell
          source={r.source_custom_attributes}
          target={r.target_custom_attributes}
          emptyLabel={t('common.empty_value', '-')}
          attributeLabelByName={attributeLabelByName}
        />
      ),
    },
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
    },
    {
      title: t('models.inventory.inventory_type'),
      key: 'inventory_type',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <DiffCell source={r.source_inventory_type_name} target={r.target_inventory_type_name} />
      ),
    },
    {
      title: t('models.inventory.batch'),
      key: 'batch',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <DiffCell source={r.source_batch} target={r.target_batch} />
      ),
    },
    {
      title: t('models.inventory.manufacturing_date'),
      key: 'manufacturing_date',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <DiffCell source={r.source_manufacturing_date} target={r.target_manufacturing_date} />
      ),
    },
    {
      title: t('models.inventory.expiration_date'),
      key: 'expiration_date',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <DiffCell source={r.source_expiration_date} target={r.target_expiration_date} />
      ),
    },
    {
      title: t('models.inventory_transformation.quantity'),
      key: 'quantity',
      align: 'right' as const,
      render: (_: unknown, r: InventoryTransformationItem) => (
        <Space size={4}>
          <SwapOutlined style={{ color: '#1890ff' }} />
          <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{r.quantity}</span>
        </Space>
      ),
    },
    {
      title: t('models.stock_adjustment.reason'),
      key: 'reason_code',
      render: (_: unknown, r: InventoryTransformationItem) =>
        r.reason_code ? t(`enums.stock_adjustment.reason_code.${r.reason_code}`, r.reason_code) : '-',
    },
    {
      title: t('models.stock_adjustment.note'),
      key: 'note',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <div>
          <div>{r.note ?? '-'}</div>
          {r.status === 'failed' && r.failure_code && (
            <div className="transformation-item-error">
              {t(`errors.${r.failure_code}`, {
                ...((r.failure_interpolations ?? {}) as Record<string, unknown>),
                defaultValue: t('errors.unexpected_error'),
              })}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <ListPageHeader
        items={['stocks', 'stocks.transformations', { label: transformation.name || transformation.id }]}
        title={
          <Space>
            {transformation.name}
            <Tag color={getStatusColor(transformation.status)}>
              {t(`enums.inventory_transformation.status.${transformation.status}`)}
            </Tag>
          </Space>
        }
        actions={
          transformation.status === 'pending' && canWrite ? (
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/inventory_transformations/${transformation.id}/edit`)}
              >
                {t('common.button.edit')}
              </Button>
              <Popconfirm
                title={t('shared.inventory_transformation.reject_confirm_title')}
                description={t('shared.inventory_transformation.reject_confirm_content', { name: transformation.name })}
                onConfirm={handleReject}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<CloseOutlined />} loading={loading}>
                  {t('common.button.reject')}
                </Button>
              </Popconfirm>
              <Popconfirm
                title={t('shared.inventory_transformation.approve_confirm_title')}
                description={t('shared.inventory_transformation.approve_confirm_content', { name: transformation.name })}
                onConfirm={handleApprove}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
              >
                <Button type="primary" icon={<CheckOutlined />} loading={loading}>
                  {t('common.button.approve')}
                </Button>
              </Popconfirm>
            </Space>
          ) : undefined
        }
      />

      {showPartialBanner && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('shared.inventory_transformation.partial_failure_banner', {
            failed: transformation.failed_count,
            total: transformation.total_items_count,
          })}
          action={
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/inventory_transformations/new')}
            >
              {t('shared.inventory_transformation.create_new_for_remediation')}
            </Button>
          }
        />
      )}

      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Descriptions title={t('shared.inventory_transformation.basic_info')} bordered>
          <Descriptions.Item label={t('models.inventory_transformation.created_at')}>
            {transformation.created_at}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.applicant')}>
            {transformation.applicant?.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.approver')}>
            {transformation.approver?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.note')} span={3}>
            {transformation.note || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {transformation.triggered_by_rule_snapshot != null && (() => {
        // 已在 services/inventoryTransformationSchemas.ts 的 RuleSnapshotSchema 做完
        // Zod 解析，這裡直接拿型別正確的物件渲染。
        const snapshot = transformation.triggered_by_rule_snapshot;
        return (
          <Card
            title={t('shared.inventory_transformation.triggered_by_rule_snapshot.title')}
            bordered={false}
            style={{ marginBottom: 24 }}
          >
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.rule_name')}>
                {snapshot.rule.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.priority')}>
                {snapshot.rule.priority}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.source_type')}>
                <Tag>{snapshot.rule.source_inventory_type.name}</Tag>
                <ArrowRightOutlined style={{ margin: '0 4px' }} />
                <Tag color="blue">{snapshot.rule.target_inventory_type.name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.threshold_days')}>
                {snapshot.matched_tier.original_expiration_date_threshold}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('shared.inventory_transformation.triggered_by_rule_snapshot.trigger_condition')}
                span={2}
              >
                {snapshot.matched_tier.trigger_mechanism === 'by_days'
                  ? t('shared.inventory_transformation.triggered_by_rule_snapshot.by_days', {
                      days: snapshot.matched_tier.days_before_expiration,
                    })
                  : t('shared.inventory_transformation.triggered_by_rule_snapshot.by_ratio', {
                      ratio: snapshot.matched_tier.expiration_ratio,
                    })}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('shared.inventory_transformation.triggered_by_rule_snapshot.evaluated_at')}
                span={2}
              >
                {dayjs(snapshot.evaluated_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        );
      })()}

      <Card title={t('shared.inventory_transformation.items')} bordered={false}>
        <Table
          columns={columns}
          dataSource={transformation.items}
          rowKey="id"
          pagination={false}
          rowClassName={(item: InventoryTransformationItem) =>
            item.status === 'failed' ? 'transformation-item-row--failed' : ''
          }
        />
      </Card>
    </div>
  );
}
